import {
  Box,
  Text,
  SimpleGrid,
  Icon,
  PopoverHeader,
  PopoverCloseButton,
  PopoverBody,
  UnorderedList,
  ListItem,
  Tooltip,
  Divider,
  Flex,
  Stack,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { IconInfo } from 'assets/icons';
import { FormikInput, FormikCheckbox, FormikRadio } from 'components/Formik';
import EmployeeList from 'components/List/EmployeeList';
import Popover from 'components/Popover';
import FEATURES from 'global/features';
import { useForwardGeocoding } from 'hooks/jobs/useForwardGeocoding';
import { useFeatureList } from 'store/useFeatureStore';
import { useCompanyInfo } from 'store/useUserStore';

import JobFormMap from '../JobFormMap';
import { initialJobFormData } from '../schema';

const JobFormGeoFence: React.FC<unknown> = () => {
  const { t } = useTranslation(['jobsTasks', 'common']);

  const { values, setFieldValue, setFieldTouched } = useFormikContext<typeof initialJobFormData>();

  const { mutateAsync: mutateAsyncForwardGeocoding } = useForwardGeocoding();

  const [markerVisible, setMarkerVisible] = useState<boolean>(false);

  const featureList: any = useFeatureList();
  const companyInfo: any = useCompanyInfo();

  useEffect(() => {
    setMarkerVisible(values?.isPrimaryAddress[0] === 'yes');
  }, [values?.isPrimaryAddress]);

  const handleBeforePrimaryAddressCheckboxClick = (
    e: React.ChangeEvent<HTMLInputElement>,
    values: typeof initialJobFormData,
    setFieldTouched: (value: string, bool: boolean) => void,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    setFieldTouched('isPrimaryAddress', true);
    if (e.target.checked) {
      const { street, city, country, state, zip_code } = values;
      const postData = {
        street,
        city,
        country,
        state,
        zip_code,
      };

      mutateAsyncForwardGeocoding(postData).then(data => {
        if (data?.payload?.latitude && data?.payload?.longitude) {
          setFieldValue('longitude', data?.payload?.longitude);
          setFieldValue('latitude', data?.payload?.latitude);
          setMarkerVisible(true);
        }
      });
    }
  };

  const unCheckUseAsPrimaryAddress = (
    values: any,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
  ) => {
    if (values.isPrimaryAddress.length) {
      setFieldValue('isPrimaryAddress', []);
    }
  };

  return (
    <Box>
      {' '}
      <Text fontSize="13px" textTransform="uppercase" fontWeight="600" color="gray.500" mb="4">
        {t('jobsTasks:locationDetails')}
      </Text>
      <SimpleGrid columns={{ md: 2 }} spacingX={{ md: 5 }}>
        <div>
          <FormikInput
            type="text"
            name="street"
            label={t('jobsTasks:street')}
            onChangeCallback={() => unCheckUseAsPrimaryAddress(values, setFieldValue)}
          />
          <SimpleGrid columns={2} spacing={2}>
            <FormikInput
              type="text"
              name="city"
              label={t('jobsTasks:city')}
              onChangeCallback={() => unCheckUseAsPrimaryAddress(values, setFieldValue)}
            />
            <FormikInput
              type="text"
              name="state"
              label={t('jobsTasks:state')}
              onChangeCallback={() => unCheckUseAsPrimaryAddress(values, setFieldValue)}
            />
            <FormikInput
              type="text"
              name="country"
              label={t('jobsTasks:country')}
              onChangeCallback={() => unCheckUseAsPrimaryAddress(values, setFieldValue)}
            />
            <FormikInput
              type="text"
              name="zip_code"
              label={t('jobsTasks:zipCode')}
              onChangeCallback={() => unCheckUseAsPrimaryAddress(values, setFieldValue)}
            />
          </SimpleGrid>
          <FormikCheckbox
            options={[{ label: t('jobsTasks:CheckBoxToRepositionAddressOnMap'), value: 'yes' }]}
            name="isPrimaryAddress"
            formControlProps={{
              mt: -3,
              mb: 1,
            }}
            checkboxProps={{
              _disabled: { opacity: `0.9 !important`, cursor: 'not-allowed' },
              isDisabled: !!(!values.street || !values.city || !values.state || !values.country || !values.zip_code),
              colorScheme: 'secondary',
            }}
            onBeforeChangeCallback={(e: React.ChangeEvent<HTMLInputElement>) => {
              handleBeforePrimaryAddressCheckboxClick(e, values, setFieldTouched, setFieldValue);
            }}
          />
          <Text color="gray.500" fontSize="13px">
            <Icon as={IconInfo} mr="1" boxSize="16px" verticalAlign="baseline" />{' '}
            {t('jobsTasks:locationDetailsHelpText')}
          </Text>
        </div>
        <Box display="flex" flexDirection="column">
          <JobFormMap
            values={values}
            setFieldValue={setFieldValue}
            // showMarker={values.street && values.city && values.state && values.country && values.zip_code}
            showMarker={markerVisible}
            // geoCodeData={geoCodeData}
            // geoCodeStatus={geoCodeStatus}
          />
        </Box>
      </SimpleGrid>
      {/* Geofence actions */}
      <Flex position="relative" justifyContent="space-evenly" flexDir={{ base: 'row', sm: 'column', md: 'row' }}>
        <Box w={{ base: '50%', sm: '100%' }}>
          <Text as="div" fontSize="13px" textTransform="uppercase" fontWeight="600" color="gray.500" mb="4">
            {t('jobsTasks:geofenceActions')}
            <Popover
              popoverTriggerEl={<Icon as={IconInfo} ml="2" boxSize="16px" verticalAlign="top" cursor="pointer" />}
            >
              <>
                <PopoverHeader textTransform="uppercase">
                  {t('jobsTasks:geofenceActions')}
                  <PopoverCloseButton />
                </PopoverHeader>

                <PopoverBody>{t('jobsTasks:geofenceActionsPopoverBody')}</PopoverBody>
              </>
            </Popover>
          </Text>
          <Text mb="2">{t('jobsTasks:geofenceActionsDesc')}</Text>
          <EmployeeList boxHeight="calc(55vh - 215px)" fieldName="geofenceMembers" addAllUsers={false} />
        </Box>
        {/* Geofence actions options */}
        <Flex ml={{ base: '5', sm: '0', md: '5' }} mt={{ base: '8', sm: '0', md: '8' }} w={{ base: '50%', sm: '100%' }}>
          <Divider orientation="vertical" borderStyle="dashed" borderColor="gray.500" mr={3} />
          <Stack>
            <Tooltip label={!featureList[FEATURES.auto_clock_in_out] ? t('common:upgradeToPro') : ''}>
              <Box>
                <FormikRadio
                  label={
                    <>
                      {t('jobsTasks:onArrival')}
                      <Popover
                        popoverTriggerEl={
                          <Icon as={IconInfo} ml="2" boxSize="16px" verticalAlign="top" cursor="pointer" />
                        }
                      >
                        <>
                          <PopoverHeader textTransform="uppercase">
                            {t('jobsTasks:onArrival')}
                            <PopoverCloseButton />
                          </PopoverHeader>
                          <PopoverBody>
                            <UnorderedList>
                              <ListItem>{t('jobsTasks:onArrivalGuide1')}</ListItem>
                              <ListItem>{t('jobsTasks:onArrivalGuide2')}</ListItem>
                              {/* <ListItem>{t('jobsTasks:onArrivalGuide3')}</ListItem> */}
                            </UnorderedList>
                          </PopoverBody>
                        </>
                      </Popover>
                    </>
                  }
                  options={[
                    { label: t('jobsTasks:doNothing'), value: '0' },
                    { label: t('jobsTasks:showSmartphoneNotification'), value: '1' },
                    ...(companyInfo?.show_autoclockin ? [{ label: t('jobsTasks:clockIn'), value: '2' }] : []),
                  ]}
                  radioProps={{
                    isDisabled: !featureList[FEATURES.auto_clock_in_out],
                    colorScheme: 'secondary',
                  }}
                  name="on_arrival"
                />
              </Box>
            </Tooltip>
            <Tooltip label={!featureList[FEATURES.auto_clock_in_out] ? t('common:upgradeToPro') : ''}>
              <Box>
                <FormikRadio
                  label={
                    <>
                      {t('jobsTasks:onDeparture')}
                      <Popover
                        popoverTriggerEl={
                          <Icon as={IconInfo} ml="2" boxSize="16px" verticalAlign="top" cursor="pointer" />
                        }
                      >
                        <>
                          <PopoverHeader textTransform="uppercase">
                            {t('jobsTasks:onDeparture')}
                            <PopoverCloseButton />
                          </PopoverHeader>
                          <PopoverBody>
                            <UnorderedList>
                              <ListItem>{t('jobsTasks:onDepartureGuide1')}</ListItem>
                              <ListItem>{t('jobsTasks:onDepartureGuide2')}</ListItem>
                              {/* <ListItem>{t('jobsTasks:onDepartureGuide3')}</ListItem> */}
                            </UnorderedList>
                          </PopoverBody>
                        </>
                      </Popover>
                    </>
                  }
                  options={[
                    { label: t('jobsTasks:doNothing'), value: '0' },
                    { label: t('jobsTasks:showSmartphoneNotification'), value: '1' },
                    ...(companyInfo?.show_autoclockin
                      ? [
                          {
                            label: t('jobsTasks:clockOut'),
                            value: '3',
                          },
                        ]
                      : []),
                  ]}
                  radioProps={{
                    isDisabled: !featureList[FEATURES.auto_clock_in_out],
                    colorScheme: 'secondary',
                  }}
                  name="on_departure"
                />
              </Box>
            </Tooltip>
          </Stack>
        </Flex>
      </Flex>
    </Box>
  );
};

export default JobFormGeoFence;
